import type { ClipboardEvent as ReactClipboardEvent, KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SheetColumnDef } from "./sheetColumns.js";

/**
 * One editable cell, shared by every sheet (#911, epic #872).
 *
 * The visual contract lives here — the same padding, the same focus ring, the
 * same selected state, the same borderless input that fills its cell — so a
 * user moving between Food and Shopping Lists is looking at the same control.
 *
 * The BEHAVIOURAL contract stays with the feature: this component reports
 * keystrokes, pastes and pointer presses upward and holds no state of its own,
 * because the two sheets legitimately answer them differently (Shopping syncs
 * in the background and supports rectangular drag selection; Food accumulates
 * edits for an explicit commit). Sharing the chrome without forcing a shared
 * state machine is what #911 means by an adapter rather than a clone.
 */
export function SheetCell<Key extends string>({
  ariaLabel,
  column,
  dataCell,
  dataCol,
  dataRow,
  disabled,
  onChangeValue,
  onKeyDown,
  onMouseDownCell,
  onMouseEnterCell,
  onPaste,
  placeholder,
  selected,
  trailing,
  value
}: {
  ariaLabel: string;
  column: SheetColumnDef<Key>;
  /** Stable selector for focus management, e.g. `${rowId}:${column}`. */
  dataCell?: string;
  dataCol?: number;
  dataRow?: number;
  disabled?: boolean;
  onChangeValue: (value: string) => void;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
  onMouseDownCell?: (event: { button: number }) => void;
  onMouseEnterCell?: (event: { buttons: number }) => void;
  onPaste?: (event: ReactClipboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  selected?: boolean;
  /** Feature-specific adornment inside the cell (an external-link icon, a
   * price note) rendered after the input. */
  trailing?: ReactNode;
  value: string;
}) {
  const { t } = useTranslation("common");
  return (
    <span
      className={selected ? "list-sheet-cell sel" : "list-sheet-cell"}
      role="gridcell"
      data-key={column.key}
      // The header label doubles as the row label on narrow screens, where CSS
      // restacks each row into a labelled card instead of a miniature grid — so
      // it is read by a person and belongs in their language.
      data-label={column.labelKey ? t(column.labelKey) : column.label}
      onMouseDown={(event) => {
        if (event.button === 0) onMouseDownCell?.({ button: event.button });
      }}
      onMouseEnter={(event) => onMouseEnterCell?.({ buttons: event.buttons })}
    >
      <input
        data-cell={dataCell}
        data-row={dataRow}
        data-col={dataCol}
        aria-label={ariaLabel}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        inputMode={column.inputMode}
        list={column.listId}
        autoComplete="off"
        onChange={(event) => onChangeValue(event.currentTarget.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
      />
      {trailing}
    </span>
  );
}

/** A cell that is not text: a select, a toggle, a rendered value. Same box,
 * same borders, same label behaviour — only the control inside differs. */
export function SheetControlCell<Key extends string>({
  children,
  column,
  selected
}: {
  children: ReactNode;
  column: SheetColumnDef<Key>;
  selected?: boolean;
}) {
  const { t } = useTranslation("common");
  return (
    <span
      className={selected ? "list-sheet-cell sel" : "list-sheet-cell"}
      role="gridcell"
      data-key={column.key}
      data-label={column.labelKey ? t(column.labelKey) : column.label}
    >
      {children}
    </span>
  );
}
